// src/services/JourneyStateService.ts
// Single source of truth for journey state.
// Reads/writes journey_states table. Emits events on changes.

import { supabase } from '@/lib/supabase';
import {
  JourneyState,
  JourneyMilestone,
  JourneyAction,
  JourneyHealthScore,
} from '@/types/Journey.types';
import { EventSystem } from './EventSystem';

export class JourneyStateService {
  private userId: string;
  private eventSystem: EventSystem;

  constructor(userId: string) {
    this.userId = userId;
    this.eventSystem = new EventSystem(userId);
  }

  async getJourneyState(): Promise<JourneyState | null> {
    try {
      const { data, error } = await supabase
        .from('journey_states')
        .select('*')
        .eq('user_id', this.userId)
        .maybeSingle();

      if (error) {
        console.error('JourneyStateService.getJourneyState error:', error);
        return null;
      }

      return data ? this.transform(data) : null;
    } catch (err) {
      console.error('JourneyStateService.getJourneyState unexpected error:', err);
      return null;
    }
  }

  async updateJourneyState(updates: Partial<JourneyState>): Promise<JourneyState | null> {
    try {
      // journey_states uses real columns: current_stage, milestones, next_actions
      const patch: Record<string, any> = {
        user_id: this.userId,
        updated_at: new Date().toISOString(),
      };

      if (updates.currentStage !== undefined) patch.current_stage = updates.currentStage;
      if (updates.milestones !== undefined) patch.milestones = updates.milestones;
      if (updates.nextActions !== undefined) patch.next_actions = updates.nextActions;

      const { data, error } = await supabase
        .from('journey_states')
        .upsert(patch, { onConflict: 'user_id' })
        .select()
        .single();

      if (error) {
        console.error('JourneyStateService.updateJourneyState error:', error);
        return null;
      }

      await this.eventSystem.emit({
        eventType: 'profile.updated',
        payload: { type: 'journey_state', updates },
        source: 'system',
        relatedId: data.id,
      });

      return this.transform(data);
    } catch (err) {
      console.error('JourneyStateService.updateJourneyState unexpected error:', err);
      return null;
    }
  }

  async updateMilestone(
    milestoneId: string,
    status: 'completed' | 'in_progress' | 'pending'
  ): Promise<JourneyState | null> {
    const current = await this.getJourneyState();
    if (!current) return null;

    const milestones = (current.milestones ?? []).map((m: JourneyMilestone) =>
      m.id === milestoneId
        ? {
            ...m,
            status,
            completedAt: status === 'completed' ? new Date().toISOString() : m.completedAt,
          }
        : m
    );

    return this.updateJourneyState({ milestones });
  }

  async completeAction(actionId: string): Promise<JourneyState | null> {
    const current = await this.getJourneyState();
    if (!current) return null;

    const nextActions = (current.nextActions ?? []).map((a: JourneyAction) =>
      a.id === actionId ? { ...a, completed: true } : a
    );

    return this.updateJourneyState({ nextActions });
  }

  async getJourneyHealth(): Promise<JourneyHealthScore> {
    const state = await this.getJourneyState();

    const milestones = state?.milestones ?? [];
    const milestoneScore = milestones.length > 0
      ? Math.round(
          (milestones.filter((m: JourneyMilestone) => m.status === 'completed').length /
            milestones.length) *
            100
        )
      : 0;

    const components: JourneyHealthScore['components'] = { milestones: milestoneScore };

    // documents: real ratio from tasks, category=documents — omitted entirely
    // if no document tasks exist yet (that's "not started", not "0%").
    const { data: docTasks } = await supabase
      .from('tasks')
      .select('status')
      .eq('user_id', this.userId)
      .eq('category', 'documents');
    if (docTasks && docTasks.length > 0) {
      const complete = docTasks.filter((t: { status: string }) => t.status === 'completed').length;
      components.documents = Math.round((complete / docTasks.length) * 100);
    }

    // financial: real financialReadinessScore from the latest finance-stage
    // event — omitted if FinanceOS has never reported one.
    const { data: financeEvents } = await supabase
      .from('journey_events')
      .select('payload, created_at')
      .eq('user_id', this.userId)
      .eq('event_type', 'profile.updated')
      .order('created_at', { ascending: false })
      .limit(20);
    const financeEvent = (financeEvents ?? []).find(
      (e: { payload: { stage?: string; financialReadinessScore?: number } }) => e.payload?.stage === 'finance'
    );
    if (financeEvent?.payload?.financialReadinessScore !== undefined) {
      components.financial = financeEvent.payload.financialReadinessScore;
    }

    const present = Object.values(components).filter((v): v is number => v !== undefined);
    const overall = present.length > 0
      ? Math.round(present.reduce((a, b) => a + b, 0) / present.length)
      : 0;

    const insights: string[] = [];
    if (components.milestones < 40) insights.push('Progress through your journey milestones');
    if (components.documents !== undefined && components.documents < 60) insights.push('Upload your required documents');
    if (components.financial !== undefined && components.financial < 60) insights.push('Complete your financial readiness assessment');

    return {
      overall,
      components,
      insights,
      recommendations: insights,
    };
  }

  private transform(row: any): JourneyState {
    return {
      id: row.id,
      userId: row.user_id,
      currentStage: row.current_stage ?? 'assessment',
      progress: 0,
      milestones: row.milestones ?? [],
      nextActions: row.next_actions ?? [],
      readinessScore: 0,
      trustScore: 0,
      context: {},
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }
}
